/**
 * Per-ADDRESS overlay cache, with the batch demoted to a transport detail.
 *
 * The batch read used to be cached under the whole ref SET
 * (`refsCacheKey(refs)` as the query key). That made the cache entry the set
 * rather than the address, so one new node — a block landing in the live feed, a
 * "Load more", a watch row arriving, a search narrowing the rows — changed the
 * key and re-resolved every address in it, including the ones answered two
 * seconds earlier. On a session watching a backfill that is a continuous full
 * re-resolve of a growing set, and
 * `address-mgmt-api-resolve_addresses_catalog` became the largest single
 * consumer of catalog read capacity in the account: ~1,300 RCU and ~27
 * BatchGetItems per invocation, ~27 invocations a minute, from ONE signed-in
 * dashboard session.
 *
 * `useInvalidateAddressOverlays` already described the shape of it — "an address
 * appears in as many keys as there are graphs on screen" — and `carried` was a
 * point patch for the one surface (the REST list) whose rows happened to arrive
 * with their own blocks.
 *
 * So: **cache per subject, batch per request.** Each `(chain, address)` is its
 * own query, and the `queryFn` does not issue a request — it enqueues the ref
 * here. Everything enqueued within one tick is coalesced into one
 * `POST /addresses/catalog/resolve`, and each waiter is handed its own slice of
 * the answer. A set that grows by one address now costs one address.
 *
 * Two consequences worth stating, because they are the point rather than a
 * side effect:
 *
 * - A ref already in cache and fresh never reaches the `queryFn` at all, so it
 *   is not in the request body either. The endpoint stops being asked about
 *   addresses nobody's view of has changed.
 * - Two surfaces on screen holding overlapping ref sets (a table and the graph
 *   above it) now share entries instead of each caching its own set under its
 *   own key. Their overlap costs one read between them, not one each.
 *
 * What is guaranteed is per REF, not per request: an address is asked about once
 * while its entry is fresh, however many surfaces want it. Whether two hooks
 * mounting together land in one flush window or two is React's scheduling and
 * deliberately not pinned — it changes the number of requests, never the number
 * of addresses in them, and the addresses are what the endpoint is charged for.
 *
 * The per-query `AbortSignal` is deliberately NOT threaded into the shared
 * request: one unmounting observer must not cancel a batch the others are still
 * waiting on, and the answer is wanted in the cache either way.
 */
import type { QueryClient } from "@tanstack/react-query";
import type { Address } from "@authe/fe-commons/addresses";
import type { AddressScope } from "@authe/fe-commons/graphql";
import {
  type AddressProxyContext,
  proxyContextKey,
  type BatchOverlays,
  type FetchAddressOverlaysBatchOptions,
  type OverlayBatchRoute,
  type OverlayRef,
  type ResolvedOverlays,
  dedupeRefs,
  fetchAddressOverlaysBatchRouted,
  isOverlayAnswerConfirmed,
  lookupAddressRow,
  lookupOverlays,
  overlayRefKey,
} from "@authe/fe-commons/overlays";
import type { AddressOverlayDiskCache } from "./addressOverlayDiskCache";

/**
 * The client type, inferred from the routed fetch rather than restated — and
 * named apart from fe-commons' own `OverlayBatchRoute` / options, which this
 * module re-uses as they are.
 */
export type OverlayBatchApiClient = Parameters<typeof fetchAddressOverlaysBatchRouted>[0];

/**
 * How far the server stood behind an answer.
 *
 * - `resolved` — it looked the address up; empty overlays mean "nobody has
 *   attributed this", which is itself an answer worth keeping.
 * - `unconfirmed` — it answered but could not vouch for it: its lookup failed
 *   and degraded to the empty block, or an older server sent an empty block
 *   (which it also sends for a failure), or the row read failed. Drawn exactly
 *   as before; never written to disk.
 * - `unanswered` — no entry came back for the ref at all (`overlays: null`).
 */
export type AddressOverlayRefStatus = "resolved" | "unconfirmed" | "unanswered";

/** One address's answer — what a single per-ref query caches. */
export interface AddressOverlayRefEntry {
  address: string;
  chainId: string | null;
  /** `null` when the resolve returned no entry for this ref (dropped/unknown). */
  overlays: ResolvedOverlays | null;
  /** See {@link AddressOverlayRefStatus}. */
  status: AddressOverlayRefStatus;
  /**
   * The stored row, and only ever a REGISTRY hit — `lookupAddressRow` already
   * withholds the synthesized default, whose schema `risk_level` would read on
   * the row as somebody having assessed the address as safe.
   *
   * Always `null` on an entry fetched without `includeRow`, which is why it is
   * part of the key below rather than something a caller can be served by
   * accident.
   */
  row: Address | null;
}

/**
 * The key ONE address's overlays are cached under.
 *
 * `includeRow`, `scope` and `withScopes` are all in it for the same reason they
 * were in the old set key: they change what the answer CONTAINS, so serving one
 * for another silently drops rows, widen hints, or a whole tenancy tier — and a
 * narrower answer served as the wider one reads as an address nobody has
 * attributed rather than as a narrower read.
 *
 * `overlayRefKey` folds the address and canonicalises the chain, so `0x38` and
 * `eip155:56` are one entry.
 */
export function addressOverlayRefQueryKey(
  ref: OverlayRef,
  includeRow: boolean,
  scope: AddressScope | undefined,
  withScopes = false,
  proxyContext?: AddressProxyContext,
): (string | boolean | null)[] {
  return [
    "address-overlay-ref",
    overlayRefKey(ref.chainId, ref.address),
    includeRow,
    scope ?? null,
    withScopes,
    ...(proxyContext ? [proxyContextKey(proxyContext)] : []),
  ];
}

// ── the coalescer ───────────────────────────────────────────────────────────

/**
 * One query's line to the disk copy (`addressOverlayDiskCache`).
 *
 * Every answer the server gives is written back; only a query with no answer in
 * memory READS — one that has an answer is being refetched on purpose (stale,
 * invalidated, a Refresh), and what it wants is the server, exactly as before
 * there was a disk. So in-session freshness is unchanged, and the disk only
 * stands in for the cold start: a reload, a new tab, a page revisited after the
 * in-memory copy was collected.
 */
export interface OverlayDiskAccess {
  cache: AddressOverlayDiskCache;
  queryClient: QueryClient;
  queryKey: readonly unknown[];
  read: boolean;
}

/**
 * The disk access for one per-ref query, or `undefined` when nothing is kept
 * (no signed-in user). `read` is decided HERE, when the query starts, because
 * that is when "does memory already hold an answer" means "is this a refetch".
 */
export function overlayDiskAccess(
  cache: AddressOverlayDiskCache | null,
  queryClient: QueryClient,
  queryKey: readonly unknown[],
): OverlayDiskAccess | undefined {
  if (!cache) return undefined;
  return { cache, queryClient, queryKey, read: queryClient.getQueryData(queryKey) === undefined };
}

interface Waiter {
  ref: OverlayRef;
  resolve: (entry: AddressOverlayRefEntry) => void;
  reject: (error: unknown) => void;
  disk?: OverlayDiskAccess;
}

interface Group {
  client: OverlayBatchApiClient;
  route: OverlayBatchRoute;
  opts: FetchAddressOverlaysBatchOptions;
  waiters: Waiter[];
}

/**
 * Client identity in the group key, so two `ApiClient`s in one process (tests,
 * a second provider) can never have their refs folded into one request.
 * A WeakMap rather than a field, because the client is not ours to stamp.
 */
const clientIds = new WeakMap<object, number>();
let nextClientId = 0;
function clientId(client: OverlayBatchApiClient): number {
  const key = client as unknown as object;
  let id = clientIds.get(key);
  if (id === undefined) {
    id = (nextClientId += 1);
    clientIds.set(key, id);
  }
  return id;
}

/** Refs only coalesce across calls that would have produced an identical body. */
function groupKey(
  client: OverlayBatchApiClient,
  route: OverlayBatchRoute,
  opts: FetchAddressOverlaysBatchOptions,
): string {
  return [
    clientId(client),
    route.transport,
    route.graphqlClient ? "gql" : "-",
    opts.includeRow ? 1 : 0,
    opts.withScopes ? 1 : 0,
    opts.scope ?? "",
    proxyContextKey(opts.proxyContext),
  ].join("|");
}

const groups = new Map<string, Group>();
let scheduled: ReturnType<typeof setTimeout> | null = null;

/**
 * Enqueue one ref and get back its own answer.
 *
 * This is the whole `queryFn` of a per-ref query. The flush is a macrotask
 * rather than a microtask on purpose: TanStack starts every query in a
 * `useQueries` set within one tick, and a macrotask window is what reliably
 * catches all of them — including the ones a sibling hook mounted in the same
 * commit, which is what makes two overlapping surfaces share a request.
 */
export function enqueueAddressOverlayRef(
  ref: OverlayRef,
  client: OverlayBatchApiClient,
  route: OverlayBatchRoute,
  opts: FetchAddressOverlaysBatchOptions = {},
  disk?: OverlayDiskAccess,
): Promise<AddressOverlayRefEntry> {
  return new Promise<AddressOverlayRefEntry>((resolve, reject) => {
    const key = groupKey(client, route, opts);
    const group = groups.get(key);
    const waiter: Waiter = { ref, resolve, reject, disk };
    if (group) group.waiters.push(waiter);
    else groups.set(key, { client, route, opts, waiters: [waiter] });
    if (scheduled === null) scheduled = setTimeout(flush, 0);
  });
}

function flush(): void {
  scheduled = null;
  const pending = [...groups.values()];
  groups.clear();
  for (const group of pending) void runGroup(group);
}

async function runGroup(group: Group): Promise<void> {
  // Several observers can want the SAME ref in one tick (a table and the graph
  // above it drawing one address). They are one entry in the request and each
  // gets the answer — the de-duplication the old set key did inside a single
  // call, now done across every caller in the window.
  const byKey = new Map<string, Waiter[]>();
  for (const waiter of group.waiters) {
    const key = overlayRefKey(waiter.ref.chainId, waiter.ref.address);
    const existing = byKey.get(key);
    if (existing) existing.push(waiter);
    else byKey.set(key, [waiter]);
  }

  // The disk answers first, for the whole window at once, so the request below
  // still carries every ref it could not vouch for in ONE body. Reading per
  // query instead would let IndexedDB's callbacks straddle the flush and split
  // one request into many.
  const answeredFromDisk = await answerFromDisk(byKey);
  const pending = [...byKey].filter(([key]) => !answeredFromDisk.has(key));
  if (pending.length === 0) return;
  const refs = pending.map(([, waiters]) => waiters[0]!.ref);

  try {
    // When the request is SENT: a catalog change stamped after this moment must
    // outrank the answer, even though the answer lands later.
    const asOf = Date.now();
    // Chunking to the endpoint's `MAX_RESOLVE_REFS` and the wave concurrency
    // both live in `fetchAddressOverlaysBatch`; this hands it the whole window.
    const batch = await fetchAddressOverlaysBatchRouted(
      group.client,
      refs,
      group.route,
      group.opts,
    );
    for (const ref of refs) {
      const entry = entryFromBatch(batch, ref);
      const waiters = byKey.get(overlayRefKey(ref.chainId, ref.address)) ?? [];
      for (const waiter of waiters) waiter.resolve(entry);
      const disk = waiters.find((waiter) => waiter.disk)?.disk;
      if (disk) void disk.cache.write(disk.queryKey, entry, asOf);
    }
  } catch (error) {
    // Every waiter hears it, and each retries on its own query's policy — which
    // re-coalesces into one request rather than N.
    for (const [, waiters] of pending) for (const waiter of waiters) waiter.reject(error);
  }
}

/**
 * Serve what the disk can vouch for; paint what it merely holds.
 *
 * A trusted answer (young, and from after the last catalog change this browser
 * heard of) resolves its waiters and never reaches the request. An untrusted one
 * is written into the query as-is, dated when the server gave it, so the row
 * paints now and the request below replaces it — TanStack keeps the fetch
 * running across a manual `setQueryData`. Returns the keys the disk answered.
 */
async function answerFromDisk(byKey: Map<string, Waiter[]>): Promise<Set<string>> {
  const answered = new Set<string>();
  await Promise.all(
    [...byKey].map(async ([key, waiters]) => {
      const disk = waiters.find((waiter) => waiter.disk?.read)?.disk;
      if (!disk) return;
      const hit = await disk.cache.read(disk.queryKey);
      if (!hit) return;
      if (hit.trusted) {
        answered.add(key);
        for (const waiter of waiters) waiter.resolve(hit.entry);
        return;
      }
      // Only where memory still has nothing: an answer that arrived meanwhile
      // is newer than anything on disk.
      if (disk.queryClient.getQueryData(disk.queryKey) === undefined) {
        disk.queryClient.setQueryData(disk.queryKey, hit.entry, { updatedAt: hit.asOf });
      }
    }),
  );
  return answered;
}

/** One ref's slice of a resolved batch. */
function entryFromBatch(batch: BatchOverlays, ref: OverlayRef): AddressOverlayRefEntry {
  const overlays = lookupOverlays(batch, ref.address, ref.chainId) ?? null;
  return {
    address: ref.address,
    chainId: ref.chainId ?? null,
    overlays,
    status:
      overlays === null
        ? "unanswered"
        : isOverlayAnswerConfirmed(batch, ref.address, ref.chainId)
          ? "resolved"
          : "unconfirmed",
    row: lookupAddressRow(batch, ref.address, ref.chainId) ?? null,
  };
}

/**
 * Write overlays that arrived with something else into the per-ref cache.
 *
 * The GraphQL address list returns each row's resolved overlays beside the rows
 * (see `useAddressesApi`), and a seed is the difference between a table that
 * draws chips for free and one that runs `POST /addresses/catalog/resolve` after
 * every page. Seeded per ref, so — unlike the seed this replaces, which was
 * written under one page's ref set — it survives "Load more", a live row, and a
 * narrowing search, and is found by any other surface asking about the same
 * address.
 *
 * Written under both `includeRow` variants because the page carried the rows
 * either way and the two are not interchangeable by contract; the `includeRow:
 * false` copy therefore has to have its row stripped rather than shared.
 * `withScopes` is not seeded at all: these blocks carry no "also defined at …"
 * hints, and an entry without them served to a caller that asked for them would
 * drop the widen affordance silently.
 */
export function seedAddressOverlayRefs(
  queryClient: QueryClient,
  refs: OverlayRef[],
  overlays: BatchOverlays,
  scope?: AddressScope,
): void {
  for (const ref of dedupeRefs(refs)) {
    const entry = entryFromBatch(overlays, ref);
    queryClient.setQueryData(addressOverlayRefQueryKey(ref, true, scope), entry);
    queryClient.setQueryData(addressOverlayRefQueryKey(ref, false, scope), {
      ...entry,
      row: null,
    });
  }
}

/** Test seam: drop anything queued but not yet flushed. */
export function __resetAddressOverlayRefQueue(): void {
  if (scheduled !== null) clearTimeout(scheduled);
  scheduled = null;
  groups.clear();
}
